import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import type { RouterOutputs } from "@alm/api-contracts";
import { EmptyState } from "@/components/app/lists/empty-state";
import { OneBuildingCard } from "@/components/app/workspace/one-building-card";
import { PageHeader } from "@/components/app/shell/page-header";
import { DataTable } from "@/components/app/patterns/data-table";
import { RelativeTime } from "@/components/app/patterns/relative-time";
import { RowActions } from "@/components/app/patterns/row-actions";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { useFacility } from "@/lib/facility/facility";
import { Eye, Link2 } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { useZone } from "@/lib/facility/zone";
import { CompanyLinkDialog } from "../-components/company-link-dialog";
import { VisitorNav } from "../-components/visitor-nav";

export const Route = createFileRoute("/_authenticated/_visitors/visitors/companies/")({
  beforeLoad: ({ context }) => guardRoute(context.me, "visitors"),
  component: VisitorCompaniesPage,
});

type CompanyRow = RouterOutputs["visitors"]["companies"]["list"][number];

function columns(zone: string): ColumnDef<CompanyRow>[] {
  return [
    {
      id: "company",
      header: "Company",
      accessorKey: "name",
      enableHiding: false,
      meta: { className: "min-w-56" },
      cell: ({ row: { original: company } }) => (
        <Link
          to="/visitors/companies/$companyKey"
          params={{ companyKey: company.key }}
          className="focus-ring rounded-sm font-medium hover:underline"
        >
          {company.name}
        </Link>
      ),
    },
    {
      id: "source",
      header: "Source",
      accessorFn: (company) => (company.linked ? "Directory" : "Typed by visitors"),
      meta: { nowrap: true, filter: "select" },
      cell: ({ row: { original: company } }) =>
        company.linked ? (
          <Badge variant="ok">
            {company.companyKind === "pharmacy" ? "Directory pharmacy" : "Directory institution"}
          </Badge>
        ) : (
          <Badge variant="warn">Typed by visitors</Badge>
        ),
    },
    {
      id: "visits",
      header: "Visits",
      accessorKey: "visits",
      meta: { nowrap: true, align: "right" },
      cell: ({ row: { original: company } }) => (
        <span className="tabular-nums">{company.visits}</span>
      ),
    },
    {
      id: "people",
      header: "People",
      accessorKey: "people",
      meta: { nowrap: true, align: "right", priority: 2 },
      cell: ({ row: { original: company } }) => (
        <span className="tabular-nums">{company.people}</span>
      ),
    },
    {
      id: "last",
      header: "Last visit",
      accessorFn: (company) => new Date(company.lastVisitAt).getTime(),
      meta: { nowrap: true },
      cell: ({ row: { original: company } }) => (
        <RelativeTime value={company.lastVisitAt} timeZone={zone} />
      ),
    },
  ];
}

/** Where visitors come from, and the typed names waiting to be filed under the directory. */
function VisitorCompaniesPage() {
  const { facilityId } = useFacility();
  const canManage = useCan("visitor:manage");
  const chosen = facilityId === "all" ? null : facilityId;
  const zone = useZone(chosen);
  const [filing, setFiling] = useState<CompanyRow | null>(null);
  const list = useQuery(orpc.visitors.companies.list.queryOptions({ input: { facilityId } }));
  const rows = list.data ?? [];
  const unlinked = rows.filter((row) => !row.linked).length;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Companies"
        count={list.isSuccess ? rows.length : undefined}
        meta={
          unlinked > 0
            ? `${unlinked} typed ${unlinked === 1 ? "name is" : "names are"} not filed in the directory yet.`
            : "Every agency, vendor and practice that has signed in, with its visits."
        }
      />
      <VisitorNav />
      {list.isError ? (
        <Card className="gap-0 py-0">
          <ReadFailed
            className="p-4"
            message="Could not load the companies."
            error={list.error}
            onRetry={() => void list.refetch()}
            retrying={list.isFetching}
          />
        </Card>
      ) : (
        <DataTable
          data={rows}
          columns={columns(zone)}
          noun="companies"
          rowId={(company) => company.key}
          pending={list.isPending}
          initialSorting={[{ id: "visits", desc: true }]}
          renderRowActions={(company) => (
            <RowActions
              label={company.name}
              modal={false}
              primary={
                canManage &&
                !company.linked &&
                chosen !== null && {
                  label: "File in directory",
                  icon: Link2,
                  onSelect: () => setFiling(company),
                }
              }
              items={[
                {
                  label: "View visits",
                  icon: Eye,
                  link: {
                    to: "/visitors/companies/$companyKey",
                    params: { companyKey: company.key },
                  },
                },
              ]}
            />
          )}
          empty={
            <EmptyState
              title="No companies yet"
              description="A company appears here the first time a visitor from it signs in."
            />
          }
        />
      )}
      {unlinked > 0 && chosen === null && canManage ? (
        <OneBuildingCard prompt="Filing a typed name under the directory happens one building at a time. Choose the building to tidy its companies." />
      ) : null}
      {filing && chosen ? (
        <CompanyLinkDialog
          facilityId={chosen}
          company={filing.name}
          onClose={() => setFiling(null)}
        />
      ) : null}
    </div>
  );
}
